import { User } from '~/models/user.model';
import { RolloutFeatureFlags } from '~/services/feature.service';
import { ITeamMember } from '~/types/model.types';
import { IUserResponse } from '~/types/server.types';
import { getCountryCodeFromMaxmind } from '~/utils/third-parties/maxmind';
import queryString from 'query-string';
import { getAcademyUrl } from '~/utils/academy.utils';
import { ACADEMY_PATHS } from '~/constants/academy.constants';

export const PartialUserProperties = [
    '_id',
    'full_name',
    'email',
    'is_vendor',
    'api_version',
    'authentication_token',
    'email_signatures',
    'company',
    'features_variations',
    'features_active',
    'initials',
    'profile_image',
    'system_user_type',
    'recurly_key',
    'account_id',
    'plaid_env',
    'plaid_public_key',
    'gift_card_feature_allowed',
    'ab_tests',
    'first_time_sent_flow_on',
    'first_time_sent_flow_test_on',
    'is_pro',
    'is_educator',
    'statsig_stable_id'
];

export function isFeatureActive(
    user: Pick<IUserResponse, 'features_active' | 'features_variations'>,
    feature: RolloutFeatureFlags,
    variation = 'on'
) {
    const isActive =
        user.features_active && user.features_active.indexOf(feature) !== -1; // rollout
    const isFeatureVariationValid =
        user.features_variations &&
        user.features_variations[feature] === variation; // splitio

    return isActive || isFeatureVariationValid;
}

export const getMemberInitials = (name?: string) => {
    if (!name) {
        return null;
    }
    const splitName = name.split(' ');
    return (
        (splitName[0]?.charAt(0).toUpperCase() || '') +
        (splitName[1]?.charAt(0).toUpperCase() || '')
    );
};

export const getClientInitials = (firstName: string, lastName: string) => {
    const firstInitial = firstName ? firstName[0] : '';
    const lastInitial = lastName ? lastName[0] : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
};

export function userToTeamMember(
    user: Pick<
        User,
        | '_id'
        | 'full_name'
        | 'email'
        | 'initials'
        | 'profile_image'
        | 'system_user_type'
        | 'address'
        | 'phone_number'
    >
): ITeamMember {
    return {
        _id: user._id,
        full_name: user.full_name,
        email: user.email,
        initials: user.initials,
        profile_image: user.profile_image,
        system_user_type: user.system_user_type,
        address: user.address,
        phone_number: user.phone_number
    };
}

/**
 * Checks if the user's location is in US or Canada using Maxmind
 * @returns Promise that resolves to true if location is in US or Canada, false otherwise
 */
export async function isLocationInUSOrCanada(): Promise<boolean> {
    const maxmindCountryCode = await getCountryCodeFromMaxmind();
    return maxmindCountryCode === 'us' || maxmindCountryCode === 'ca';
}

export async function isUSOrCanadaBased(
    user: User | null | undefined
): Promise<boolean> {
    if (!user) {
        // If no user data is available, fall back to maxmind check
        return isLocationInUSOrCanada();
    }

    const countryCodes = [
        user.hb_location?.country_code,
        user.attribution?.first_login_location?.country_code
    ].map(code => code?.toLowerCase());

    // If we have location data in the user object
    if (countryCodes.some(code => code === 'us' || code === 'ca')) {
        return true;
    }

    // If we have any country codes that aren't US/CA
    if (countryCodes.some(code => code !== undefined)) {
        return false;
    }

    // If we reach here, we don't have location data - defer to external check
    return isLocationInUSOrCanada();
}

export function getWebinarLink(hasFlows: boolean) {
    if (hasFlows) {
        return getAcademyUrl(ACADEMY_PATHS.WEBINAR_LINK);
    }
    return 'https://www.bigmarker.com/honeybook/HoneyBook-Trialer-Webinar';
}

export function isUserFullyLoaded(user: User): boolean {
    return (
        !!user?.ab_tests &&
        user?.account &&
        Object.keys(user.account).length > 0
    );
}

export const getTrialDataFromQS = () => {
    const qs = queryString.parse(window.location.search);

    const trialData = {
        extend_trial: qs.extend_trial,
        trial_type: qs.trial_type,
        utm_campaign: qs.utm_campaign
    };

    return trialData;
};
